import { type MatFallbackReason } from "../../../utils/tag-manager";

/** Was der Browser ueber die Ladebedingungen der 3D-Figur verraet. */
export type LoadEnvironment = {
    reducedMotion: boolean;
    saveData: boolean;
    reducedData: boolean;
    /** Ein frueherer Startseitenaufruf dieses Besuchs war zu langsam (`slow_frames`). */
    slowEarlier: boolean;
};

/**
 * Grund, aus dem die 3D-Figur gar nicht erst laedt — oder `null`, wenn sie
 * laden darf. Reihenfolge = Vorrang: reduzierte Bewegung vor Datensparen, damit
 * ein Besuch mit beidem dem Wunsch nach Ruhe zugeordnet wird.
 *
 * `prefers-reduced-data` zaehlt als `save_data`: dieselbe Absicht, und die
 * Telemetrie kennt keinen eigenen Grund dafuer.
 *
 * WebGL2 fehlt (`no_webgl`) prueft der Aufrufer erst, wenn der Slot im
 * Viewport ist — die Probe legt einen Grafikkontext an.
 */
export const blockingReason = (env: LoadEnvironment): MatFallbackReason | null => {
    if (env.reducedMotion) {
        return "reduced_motion";
    }
    if (env.saveData || env.reducedData) {
        return "save_data";
    }
    if (env.slowEarlier) {
        return "slow_frames";
    }
    return null;
};

type NetworkInformationLike = { saveData?: boolean };

const matches = (query: string): boolean => {
    try {
        return window.matchMedia?.(query).matches ?? false;
    } catch {
        return false;
    }
};

export const MAT3D_SLOW_STORAGE_KEY = "quaweb.mat3dSlow";

/** Liest die Ladebedingungen aus dem Browser. */
export const readLoadEnvironment = (): LoadEnvironment => {
    const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
    let slowEarlier = false;
    try {
        slowEarlier = window.sessionStorage.getItem(MAT3D_SLOW_STORAGE_KEY) === "1";
    } catch {
        slowEarlier = false;
    }
    return {
        reducedMotion: matches("(prefers-reduced-motion: reduce)"),
        saveData: connection?.saveData === true,
        reducedData: matches("(prefers-reduced-data: reduce)"),
        slowEarlier,
    };
};

/** Merkt sich fuer den Rest des Besuchs, dass die Figur zu langsam war. */
export const rememberSlowFrames = (): void => {
    try {
        window.sessionStorage.setItem(MAT3D_SLOW_STORAGE_KEY, "1");
    } catch {
        /* Speicher gesperrt: der naechste Aufruf misst neu. */
    }
};
